import { Currency, IProductDetail } from '@/api/types/public';
import { IImageListItem } from '@/components/GoodsDetail/handleData';
import { colors } from '@/theme/zeelool.theme';
import { createTranslator } from 'next-intl';
import { formatPriceNumberWithSymbol } from './price';

interface CouponData {
  way: number;
  order_paid?: number;
  discounts_details?: {
    discounts_type?: number;
    discounts_value?: string | number;
    discounts_threshold?: string | number;
    discounts_threshold_type?: number;
  };
  full_discounts_details?: {
    discounts_type?: number;
    discounts_value?: string | number;
    discounts_threshold?: string | number;
  };
  is_get?: number;
}

// 处理有/无订单支付的情况
const hasOrderPaid = (data: CouponData, t: ReturnType<typeof createTranslator>): string => {
  const { discounts_details, order_paid } = data;
  if (order_paid === 1) {
    // 用户有已支付订单
    return t('goodsDetail.coupon.percentOffSitewideSale', {
      percent: discounts_details?.discounts_value,
    });
  } else if (order_paid === 2) {
    // 用户无已支付订单
    return t('goodsDetail.coupon.percentOffFirstOrder', {
      percent: discounts_details?.discounts_value,
    });
  }
  return '';
};

// 处理折扣类型优惠券
const processDiscount = (
  data: CouponData,
  currency: Currency,
  t: ReturnType<typeof createTranslator>,
): string => {
  const discountsDetails = data.discounts_details;
  if (!discountsDetails) return '';

  if (discountsDetails.discounts_threshold_type === 1) {
    // 按金额
    if (Number(discountsDetails.discounts_threshold) === 0.01) {
      return hasOrderPaid(data, t);
    } else {
      return `${t('goodsDetail.coupon.percentOffOrdersOver', { percent: discountsDetails.discounts_value })} ${formatPriceNumberWithSymbol(discountsDetails.discounts_threshold.toString(), currency, false, 2, false)}`;
    }
  } else if (discountsDetails.discounts_threshold_type === 2) {
    // 按件数
    if (Number(discountsDetails.discounts_threshold) === 1) {
      return hasOrderPaid(data, t);
    } else {
      // return `${discountsDetails.discounts_value}% OFF ${discountsDetails.discounts_threshold}+ Items`;
      return t('goodsDetail.coupon.percentOffItems', {
        percent: discountsDetails.discounts_value,
        number: discountsDetails.discounts_threshold,
      });
    }
  }
  return '';
};

// 处理满减类型优惠券
const processFullDiscount = (
  data: CouponData,
  currency: Currency,
  t: ReturnType<typeof createTranslator>,
): string => {
  const fullDiscountsDetails = data.full_discounts_details;
  if (!fullDiscountsDetails) return '';

  // 计算差值 (threshold - value)
  const difference =
    Number(fullDiscountsDetails.discounts_threshold) - Number(fullDiscountsDetails.discounts_value);
  const price = formatPriceNumberWithSymbol(
    fullDiscountsDetails.discounts_value.toString(),
    currency,
    false,
    2,
    false,
  );
  if (Number(difference.toFixed(2)) === 0.01) {
    if (data.order_paid === 1) {
      // 用户有已支付订单
      // return `${formatPriceNumberWithSymbol(fullDiscountsDetails.discounts_value.toString(), currency, false, 0)} OFF; Sitewide Sale`;

      return t('goodsDetail.coupon.priceOffSitewideSale', { price });
    } else if (data.order_paid === 2) {
      // 用户无已支付订单
      // return `${formatPriceNumberWithSymbol(fullDiscountsDetails.discounts_value.toString(), currency, false, 0)} OFF; First Order`;
      return t('goodsDetail.coupon.priceOffFirstOrder', { price });
    }
  } else {
    // return `${formatPriceNumberWithSymbol(fullDiscountsDetails.discounts_value.toString(), currency, false, 0)} OFF Orders over ${formatPriceNumberWithSymbol(fullDiscountsDetails.discounts_threshold.toString(), currency, false, 0)}`;
    return t('goodsDetail.coupon.priceOffOrdersOver', {
      price,
      priceLimit: formatPriceNumberWithSymbol(
        fullDiscountsDetails.discounts_threshold.toString(),
        currency,
        false,
        2,
        false,
      ),
    });
  }
  return '';
};

// 主要的优惠券提示处理函数
export const processCouponTips = (
  couponData: CouponData,
  currency: Currency,
  t: ReturnType<typeof createTranslator>,
): string => {
  if ([1, 5].includes(couponData.way) && couponData?.discounts_details?.discounts_type === 1) {
    // 折扣 && 一次性折扣
    return processDiscount(couponData, currency, t);
  } else if (couponData.way === 2 && couponData?.full_discounts_details?.discounts_type === 1) {
    // 满减 && 一次性满减
    return processFullDiscount(couponData, currency, t);
  }
  return '';
};

export type { CouponData };

export const getPromotionStyle = (extendedProduct: IProductDetail) => {
  const { activity_style } = extendedProduct;
  if (!activity_style) {
    return {
      backgroundColor: colors.auxiliaryRoseRed,
    };
  }

  if (activity_style.flash_killing_color_consistent === 1) {
    return { backgroundColor: activity_style.flash_killing_single_color };
  } else if (activity_style.flash_killing_color_consistent === 2) {
    return {
      background: `linear-gradient(90deg, ${activity_style.flash_killing_a_color}, ${activity_style.flash_killing_b_color})`,
    };
  }
  return {
    backgroundColor: colors.auxiliaryRoseRed,
  };
};

export const scrollToReviewList = (distance: number) => {
  const anchor = document.querySelector('#customer-reviews-list');
  if (anchor) {
    const rect = anchor.getBoundingClientRect();
    const scrollTop = window.pageYOffset + rect.top - (distance || 200);
    window.scrollTo({
      top: scrollTop,
      behavior: 'smooth',
    });
  }
};

// 获取商品的收藏数量
export function getWishNumber(item) {
  const { wish_num = '' } = item || {};
  if (wish_num === '' || isNaN(Number(wish_num))) {
    return '';
  }
  return Number(wish_num);
}
// 获取评价数量
export function getReviewNum(reviewsData) {
  const { count = '' } = reviewsData || {};
  if (count === '' || isNaN(Number(count))) {
    return '';
  }
  return Number(count);
}
// 获取评价分数
export function getReviewRate(reviewsData) {
  const { stars = '' } = reviewsData || {};
  if (stars === '' || isNaN(Number(stars))) {
    return '';
  }
  return Number(stars);
}
// 获取Spu评论数量
export function getSpuReviewNum(item) {
  const { spu_review_count = '' } = item || {};
  if (spu_review_count === '' || isNaN(Number(spu_review_count))) {
    return '';
  }
  return Number(spu_review_count);
}
// 获取spu评分
export function getSpuRate(item) {
  const { spu_review_stars = '' } = item || {};
  if (spu_review_stars === '' || isNaN(Number(spu_review_stars))) {
    return '';
  }
  return Number(spu_review_stars);
}

// 判断是否需要修改图片背景色值
export function isChangeGreyImg(item: IImageListItem, imgArr: IImageListItem[]) {
  try {
    return (
      ['product'].includes(item.goodsType) ||
      (item.goodsType === 'product_detail' &&
        !imgArr.some((img) => img.goodsType === 'product_gray'))
    );
  } catch {
    return true;
  }
}
